/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import './LandingPage.css';
import './Nogomandala.css';
import { useSeo } from '../hooks/useSeo';
import PublicPageNav from './PublicPageNav';
import {
  PLAYERS,
  pickDailyPlayer,
  puzzleNumber,
  buildComparison,
  searchPlayers,
  findPlayerByName,
  isWinningGuess,
} from '../data/nogomandalaPlayers';


const STORAGE_KEY = 'nogomandala-state-v2';

const GAME_MODES = {
  all: {
    label: 'Svi igrači',
    description: 'Miks HNL-a, reprezentacije i globalnih zvijezda.',
    pool: (players) => players,
  },
  hnl: {
    label: 'Samo HNL',
    description: 'Dnevni puzzle bira samo igrače iz HNL klubova.',
    pool: (players) => players.filter((p) => p.liga === 'HNL'),
  },
};

const DIFFICULTIES = {
  easy: { label: 'Lagano', maxGuesses: 10, hintAfter: 2 },
  normal: { label: 'Normalno', maxGuesses: 8, hintAfter: 3 },
  hard: { label: 'Teško', maxGuesses: 6, hintAfter: 4 },
};

const ATTRS = ['klub', 'liga', 'pozicija', 'nacionalnost', 'dob', 'brojDresa', 'visinaCm'];
const NOGOMANDALA_URL = 'https://statarena.app/nogomandala';
const NOGOMANDALA_DESCRIPTION =
  'Igraj Nogomandalu, dnevnu nogometnu igru pogađanja. Pogodi HNL ili svjetskog nogometaša kroz klub, ligu, poziciju, dob, broj dresa i visinu.';
const NOGOMANDALA_STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Nogomandala',
  url: NOGOMANDALA_URL,
  applicationCategory: 'GameApplication',
  operatingSystem: 'Web',
  description: NOGOMANDALA_DESCRIPTION,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
  },
  publisher: {
    '@type': 'Organization',
    name: 'StatArena',
    url: 'https://statarena.app/',
  },
};

function todayKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // ignore
  }
}

function matchEmoji(match, dir) {
  if (match === 'eq') return '🟩';
  if (match === 'near') {
    if (dir === 'up') return '🟨🔼';
    if (dir === 'down') return '🟨🔽';
    return '🟨';
  }
  if (dir === 'up') return '⬜🔼';
  if (dir === 'down') return '⬜🔽';
  return '⬜';
}

function buildShareString(history, target, num, won, options = {}) {
  const {
    maxGuesses = 8,
    modeLabel = 'Svi igrači',
    difficultyLabel = 'Normalno',
    hintUsed = false,
  } = options;
  const hintMark = hintUsed ? ' · hint' : '';
  const head = `Nogomandala #${num} · ${modeLabel} · ${difficultyLabel}${hintMark} – ${won ? `${history.length}/${maxGuesses}` : `X/${maxGuesses}`} ⚽`;
  const rows = history.map((h) => {
    const c = h.cmp;
    return ATTRS.map((attr) => {
      const cell = c[attr];
      return matchEmoji(cell.match, cell.dir);
    }).join('');
  });
  return `${head}\n\n${rows.join('\n')}\n\nigraj na: statarena.app/nogomandala`;
}

const Nogomandala = () => {
  useSeo({
    title: 'Nogomandala - HNL nogometni Wordle | Pogodi nogometaša',
    description: NOGOMANDALA_DESCRIPTION,
    canonical: NOGOMANDALA_URL,
    robots: 'index, follow',
    ogTitle: 'Nogomandala - dnevna igra pogađanja nogometaša',
    ogDescription: NOGOMANDALA_DESCRIPTION,
    ogUrl: NOGOMANDALA_URL,
    twitterTitle: 'Nogomandala - HNL nogometni Wordle',
    twitterDescription: NOGOMANDALA_DESCRIPTION,
    structuredData: NOGOMANDALA_STRUCTURED_DATA,
  });

  const today = useMemo(() => new Date(), []);
  const dayKey = useMemo(() => todayKey(today), [today]);
  const [mode, setMode] = useState('hnl');
  const [difficulty, setDifficulty] = useState('normal');
  const [hintUsed, setHintUsed] = useState(false);
  const modeConfig = GAME_MODES[mode] || GAME_MODES.all;
  const difficultyConfig = DIFFICULTIES[difficulty] || DIFFICULTIES.normal;
  const playerPool = useMemo(() => modeConfig.pool(PLAYERS), [modeConfig]);
  const target = useMemo(() => pickDailyPlayer(today, playerPool), [today, playerPool]);
  const num = useMemo(() => puzzleNumber(today), [today]);
  const gameKey = `${dayKey}:${mode}:${difficulty}`;
  const maxGuesses = difficultyConfig.maxGuesses;

  const [query, setQuery] = useState('');
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState('playing'); // 'playing' | 'won' | 'lost'
  const [error, setError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const hint = useMemo(() => {
    if (!target) return '';
    const hints = [
      `Liga: ${target.liga}`,
      `Pozicija: ${target.subPozicija}`,
      `Kontinent reprezentacije: ${target.kontinent}`,
      `Visina je ${target.visinaCm >= 185 ? '185 cm ili više' : 'ispod 185 cm'}`,
      `Broj dresa je ${target.brojDresa >= 20 ? '20 ili veći' : 'ispod 20'}`,
    ];
    return hints[num % hints.length];
  }, [num, target]);

  // Učitaj iz localStorage za današnji dan i odabrane postavke
  useEffect(() => {
    const saved = loadState();
    if (saved && saved.gameKey === gameKey) {
      setHistory(saved.history || []);
      setStatus(saved.status || 'playing');
      setHintUsed(Boolean(saved.hintUsed));
    } else {
      setHistory([]);
      setStatus('playing');
      setHintUsed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameKey]);

  // Spremi u localStorage
  useEffect(() => {
    saveState({ gameKey, history, status, hintUsed });
  }, [gameKey, history, status, hintUsed]);

  const suggestions = useMemo(() => {
    if (!query || query.length < 1) return [];
    const guessed = new Set(history.map((h) => h.player.id));
    return searchPlayers(query, 8, playerPool).filter((p) => !guessed.has(p.id));
  }, [query, history, playerPool]);

  const submitGuess = useCallback(
    (player) => {
      if (status !== 'playing') return;
      if (!player) {
        setError('Nije pronađen igrač s tim imenom.');
        return;
      }
      if (history.some((h) => h.player.id === player.id)) {
        setError('Već si pokušao s tim igračem.');
        return;
      }
      setError('');
      const cmp = buildComparison(player, target, today);
      const newHistory = [...history, { player, cmp }];
      setHistory(newHistory);
      setQuery('');
      setShowSuggestions(false);
      setActiveIdx(-1);

      if (isWinningGuess(player, target)) {
        setStatus('won');
      } else if (newHistory.length >= maxGuesses) {
        setStatus('lost');
      }
    },
    [history, maxGuesses, status, target, today],
  );

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && activeIdx >= 0 && suggestions[activeIdx]) {
        submitGuess(suggestions[activeIdx]);
      } else if (suggestions[0]) {
        submitGuess(suggestions[0]);
      } else if (query.trim()) {
        const p = findPlayerByName(query.trim(), playerPool);
        if (p) submitGuess(p);
        else setError('Nije pronađen igrač. Pokušaj drugačije ime.');
      }
    }
  };

  const handleShare = async () => {
    const text = buildShareString(history, target, num, status === 'won', {
      maxGuesses,
      modeLabel: modeConfig.label,
      difficultyLabel: difficultyConfig.label,
      hintUsed,
    });
    try {
      if (navigator.share) {
        await navigator.share({ text });
        return;
      }
    } catch (e) {
      // ignore - fallback below
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      // last resort - prompt
      window.prompt('Kopiraj rezultat:', text);
    }
  };

  const remaining = maxGuesses - history.length;
  const canShowHint = history.length >= difficultyConfig.hintAfter;
  const lockSettings = history.length > 0 && status === 'playing';

  return (
    <div className="landing nogomandala-page">
      <PublicPageNav />

      <main className="nogomandala-shell">
        <div className="nogomandala-header">
          <h1>Nogomandala</h1>
          <p>
            Pogodi tajnog nogometaša u <strong>{maxGuesses} pokušaja</strong>. Svaki dan
            novi igrač. Boje ti govore koliko si blizu: zelena = točno, žuta =
            blizu, siva = krivo. Strelice za skalarne vrijednosti pokazuju je li
            tajni igrač viši, stariji ili ima veći broj.
          </p>
          <div className="nogomandala-meta">
            <span>Puzzle #{num}</span>
            <span>·</span>
            <span>{dayKey}</span>
            <span>·</span>
            <span>{playerPool.length}/{PLAYERS.length} igrača u modu</span>
          </div>
        </div>

        <div className="nogomandala-board">
          <div className="nogomandala-settings">
            <div className="nogomandala-setting-group" aria-label="Mod igre">
              {Object.entries(GAME_MODES).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  className={`nogomandala-chip ${mode === key ? 'is-active' : ''}`}
                  onClick={() => setMode(key)}
                  disabled={lockSettings}
                  title={config.description}
                >
                  {config.label}
                </button>
              ))}
            </div>
            <div className="nogomandala-setting-group" aria-label="Težina">
              {Object.entries(DIFFICULTIES).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  className={`nogomandala-chip ${difficulty === key ? 'is-active' : ''}`}
                  onClick={() => setDifficulty(key)}
                  disabled={lockSettings}
                  title={`${config.maxGuesses} pokušaja, hint nakon ${config.hintAfter}.`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>

          {status === 'playing' && (
            <div className="nogomandala-input-wrap">
              <div className="nogomandala-input-row">
                <input
                  ref={inputRef}
                  type="text"
                  className="nogomandala-input"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowSuggestions(true);
                    setActiveIdx(-1);
                    setError('');
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => {
                    // delay to allow click
                    setTimeout(() => setShowSuggestions(false), 150);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Upiši ime igrača (npr. Modrić, Livaja, Messi)..."
                  autoComplete="off"
                  aria-label="Ime igrača"
                />
                <button
                  type="button"
                  className="btn btn-primary nogomandala-submit"
                  onClick={() => {
                    if (suggestions[0]) submitGuess(suggestions[0]);
                    else if (query.trim()) {
                      const p = findPlayerByName(query.trim(), playerPool);
                      if (p) submitGuess(p);
                      else setError('Nije pronađen igrač. Pokušaj drugačije ime.');
                    }
                  }}
                  disabled={!query.trim()}
                >
                  Pogodi
                </button>
              </div>

              {showSuggestions && suggestions.length > 0 && (
                <ul className="nogomandala-suggestions" role="listbox">
                  {suggestions.map((p, idx) => (
                    <li
                      key={p.id}
                      className={`nogomandala-suggestion ${idx === activeIdx ? 'is-active' : ''}`}
                      role="option"
                      aria-selected={idx === activeIdx}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        submitGuess(p);
                      }}
                      onMouseEnter={() => setActiveIdx(idx)}
                    >
                      <span className="suggestion-name">{p.ime}</span>
                      <span className="suggestion-club">{p.klub}</span>
                    </li>
                  ))}
                </ul>
              )}

              {error && <div className="nogomandala-error">{error}</div>}
              <div className="nogomandala-remaining">
                Preostalo pokušaja: <strong>{remaining}</strong>
              </div>
              <div className="nogomandala-hint">
                {hintUsed ? (
                  <span>{hint}</span>
                ) : (
                  <button
                    type="button"
                    className="nogomandala-hint-button"
                    onClick={() => setHintUsed(true)}
                    disabled={!canShowHint}
                  >
                    {canShowHint
                      ? 'Otvori hint'
                      : `Hint nakon ${difficultyConfig.hintAfter}. pokušaja`}
                  </button>
                )}
              </div>
            </div>
          )}

          {status !== 'playing' && (
            <div className={`nogomandala-result is-${status}`}>
              {status === 'won' ? (
                <>
                  <h2>🏆 Bravo!</h2>
                  <p>
                    Pogodio si <strong>{target.ime}</strong> u {history.length}/{maxGuesses} pokušaja.
                  </p>
                </>
              ) : (
                <>
                  <h2>Game over</h2>
                  <p>
                    Tajni igrač je bio: <strong>{target.ime}</strong> ({target.klub}).
                    Sutra novi puzzle!
                  </p>
                </>
              )}
              <div className="nogomandala-actions">
                <button type="button" className="btn btn-primary" onClick={handleShare}>
                  {copied ? 'Kopirano! ✓' : 'Podijeli rezultat'}
                </button>
                <Link to="/" className="btn">Početna</Link>
              </div>
            </div>
          )}

          {history.length > 0 && (
            <div className="nogomandala-table-wrap">
              <table className="nogomandala-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Igrač</th>
                    <th>Klub</th>
                    <th>Liga</th>
                    <th>Pozicija</th>
                    <th>Nacionalnost</th>
                    <th>Dob</th>
                    <th>Broj</th>
                    <th>Visina</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i}>
                      <td className="cell-num">{i + 1}</td>
                      <td className="cell-name">{h.player.ime}</td>
                      {ATTRS.map((attr) => {
                        const cell = h.cmp[attr];
                        const cls = `cell cell-${cell.match}`;
                        let arrow = '';
                        if (cell.dir === 'up') arrow = ' ▲';
                        else if (cell.dir === 'down') arrow = ' ▼';
                        return (
                          <td key={attr} className={cls}>
                            <span className="cell-val">{cell.value}</span>
                            {cell.match !== 'eq' && cell.dir && (
                              <span className="cell-arrow" aria-hidden="true">{arrow}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <section className="nogomandala-howto">
          <h2>Kako se igra?</h2>
          <ol>
            <li>Upiši ime nogometaša koji misliš da je tajni igrač.</li>
            <li>
              Sustav usporedi <strong>klub, ligu, poziciju, nacionalnost, dob, broj
              dresa i visinu</strong>.
            </li>
            <li>
              <span className="legend-dot legend-eq" /> <strong>Zeleno</strong> = točno.{' '}
              <span className="legend-dot legend-near" /> <strong>Žuto</strong> = blizu
              (susjedna pozicija, isti kontinent, ±2 godine, ±3 broj/cm).{' '}
              <span className="legend-dot legend-no" /> <strong>Sivo</strong> = krivo.
            </li>
            <li>Strelica gore (▲) znači da tajni igrač ima <em>veću</em> vrijednost.</li>
            <li>Broj pokušaja ovisi o težini. Sutra u ponoć novi puzzle.</li>
          </ol>
        </section>

        <section className="nogomandala-seo-copy" aria-labelledby="nogomandala-about-title">
          <h2 id="nogomandala-about-title">Nogometni Wordle za HNL i svjetske igrače</h2>
          <p>
            Nogomandala je dnevna nogometna igra pogađanja u kojoj tražiš tajnog
            nogometaša pomoću tragova iz stvarnog nogometa. Svaki pokušaj uspoređuje
            klub, ligu, poziciju, nacionalnost, dob, broj dresa i visinu, pa se igra
            ponaša kao nogometni Wordle za navijače koji vole HNL, reprezentaciju i
            najveće svjetske klubove.
          </p>
          <p>
            Možeš igrati HNL mod s igračima iz hrvatske lige ili širi mod sa svim
            igračima u bazi. Novi puzzle dolazi svaki dan u lokalnu ponoć, a težinu
            možeš prilagoditi ovisno o tome želiš li lagani, normalni ili teži izazov.
          </p>
        </section>
      </main>
    </div>
  );
};

export default Nogomandala;

